import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faPhone, faTimes, faUserPlus, faIdCard, faBuilding, faEnvelope, faStore, faCheck, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { useLoginStore } from '../../../store/useLogin';
import RegistrationErrorModal from './RegistrationErrorModal';

const RegistrationModal = ({
  isRegistration,
  setIsRegistration,
}: {
  isRegistration: boolean;
  setIsRegistration: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { register } = useLoginStore();

  const [id, setId] = useState<string>('');
  const [pw, setPw] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [pwCheck, setPwCheck] = useState<string>('');
  const [businessNumber, setBusinessNumber] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [ceoName, setCeoName] = useState<string>('');
  const [companyTelNo, setCompanyTelNo] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [bizProfileName, setBizProfileName] = useState<string>('');
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const onChangeId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setId(e.target.value.trim());
  };
  const onChangePw = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPw(e.target.value);
  };
  const onChangePwCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPwCheck(e.target.value);
  };
  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value.trim());
  };
  const onChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneInput = e.target.value.replace(/\D/g, '');

    if (phoneInput.length > 3 && phoneInput.length <= 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(3)}`;
    } else if (phoneInput.length > 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(
        3,
        7
      )}-${phoneInput.slice(7, 11)}`;
    }

    setPhone(phoneInput);
  };
  const onChangeBusinessNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    let businessInput = e.target.value.replace(/\D/g, '');

    if (businessInput.length > 3 && businessInput.length <= 5) {
      businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(3)}`;
    } else if (businessInput.length > 5) {
      businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(
        3,
        5
      )}-${businessInput.slice(5, 10)}`;
    }

    setBusinessNumber(businessInput);
  };
  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const onChangeCompanyName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyName(e.target.value.trim());
  };

  const onChangeCeoName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCeoName(e.target.value.trim());
  };
  const onChangeCompanyTelNo = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneInput = e.target.value.replace(/\D/g, '');

    if (phoneInput.length > 3 && phoneInput.length <= 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(3)}`;
    } else if (phoneInput.length > 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(
          3,
          7
      )}-${phoneInput.slice(7, 11)}`;
    }

    setCompanyTelNo(phoneInput);
  };
  const onChangeAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
  };

  const onChangeBizProfileName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBizProfileName(e.target.value.trim());
  };

  const onClickClose = useCallback(() => {
    setIsRegistration(false);
    // 모달 닫을 때 폼 초기화
    setId('');
    setPw('');
    setName('');
    setPhone('');
    setPwCheck('');
    setBusinessNumber('');
    setCeoName('');
    setCompanyName('');
    setCompanyTelNo('');
    setAddress('');
    setEmail('');
    setBizProfileName('');
    setAgreeToTerms(false);
  }, [setIsRegistration]);

  const onClickRegister = useCallback(async () => {
    if (!id || !pw || !name || !phone || !pwCheck || !businessNumber || !email || !bizProfileName
      || !companyName || !companyTelNo || !address
    ) {
      setErrorMessage('모든 항목을 입력해 주세요.');
      setShowErrorModal(true);
      return;
    }
    
    if (!agreeToTerms) {
      setErrorMessage('이용약관에 동의해 주세요.');
      setShowErrorModal(true);
      return;
    }
    if (pw !== pwCheck) {
      setErrorMessage('비밀번호가 일치하지 않습니다.');
      setShowErrorModal(true);
      return;
    }
    
    // 사업자등록번호 유효성 검사 (10자리)
    const businessNumRegex = /^\d{3}-\d{2}-\d{5}$/;
    if (!businessNumRegex.test(businessNumber)) {
      setErrorMessage('올바른 사업자등록번호 형식이 아닙니다. (예: 123-45-67890)');
      setShowErrorModal(true);
      return;
    }
    
    // 이메일 유효성 검사 (앞뒤 공백 제거 후 검사)
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setErrorMessage('올바른 이메일 형식이 아닙니다.');
      setShowErrorModal(true);
      return;
    }
    
    // 당근 비즈프로필명 길이 검사
    if (bizProfileName.length < 2 || bizProfileName.length > 20) {
      setErrorMessage('브랜드 명은 2자 이상 20자 이하로 입력해 주세요.');
      setShowErrorModal(true);
      return;
    }
    
    const result = await register(id, pw, name, phone, businessNumber, trimmedEmail, bizProfileName, companyName, companyTelNo, address, ceoName);
    if (result.success) {
      onClickClose();
    } else {
      setErrorMessage(result.message);
      setShowErrorModal(true);
    }
  }, [id, name, onClickClose, phone, pw, pwCheck, register, businessNumber, email, bizProfileName, agreeToTerms, companyName, companyTelNo, address, ceoName]);

  // ESC key event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClickClose();
      }
    };

    if (isRegistration) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRegistration, onClickClose]);
  
  if (!isRegistration) return null;
  
  return (
    <div className="!fixed !inset-0 !z-50 !flex !items-center !justify-center !p-4 !bg-black !bg-opacity-50 !overflow-y-auto">
      <div className="!bg-white !rounded-2xl !shadow-2xl !w-full !max-w-md !relative !animate-fadeIn !my-8">
        {/* 닫기 버튼 */}
        <button
          onClick={onClickClose}
          className="!absolute !top-4 !right-4 !w-10 !h-10 !rounded-full !bg-gray-100 hover:!bg-gray-200 !flex !items-center !justify-center !transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} className="!text-gray-600" />
        </button>
        
        {/* 헤더 */}
        <div className="!px-8 !pt-8 !pb-4 !text-center">
          <div className="!inline-flex !items-center !justify-center !w-16 !h-16 !bg-gradient-to-br !from-indigo-500 !to-purple-600 !rounded-full !mb-4">
            <FontAwesomeIcon icon={faUserPlus} className="!text-white !text-2xl" />
          </div>
          <h2 className="!text-2xl !font-bold !text-gray-800">회원가입</h2>
          <p className="!text-gray-600 !mt-2">Fortune LAB 서비스에 오신 것을 환영합니다</p>
        </div>
        
        {/* 폼 */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onClickRegister();
          }}
          className="!px-8 !pb-8 !space-y-4 !max-h-[60vh] !overflow-y-auto"
        >
          {/* 회원정보 섹션 */}
          <div className="!mb-6">
            <h3 className="!text-lg !font-semibold !text-gray-800 !mb-4 !flex !items-center">
              <FontAwesomeIcon icon={faUser} className="!mr-2 !text-indigo-500" />
              회원정보
            </h3>
            
          {/* 이름 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">이름</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faIdCard} className="!text-gray-400" />
              </div>
              <input
                type="text"
                value={name}
                onChange={onChangeName}
                placeholder="이름을 입력해 주세요"
                className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                required
              />
            </div>
          </div>
          
          {/* 전화번호 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">전화번호</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faPhone} className="!text-gray-400" />
              </div>
              <input
                type="tel"
                value={phone}
                onChange={onChangePhone}
                placeholder="전화번호를 입력해 주세요"
                className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                required
              />
            </div>
          </div>
          
          {/* 아이디 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">아이디</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faUser} className="!text-gray-400" />
              </div>
              <input
                type="text"
                value={id}
                onChange={onChangeId}
                placeholder="아이디를 입력해 주세요"
                className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                required
              />
            </div>
          </div>
          
          {/* 비밀번호 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">
              비밀번호
            </label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faLock} className="!text-gray-400" />
              </div>
              <input
                type="password"
                value={pw}
                onChange={onChangePw}
                placeholder="비밀번호를 입력해 주세요"
                className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                style={{ fontFamily: 'sans-serif', letterSpacing: '0.1em' }}
                required
              />
            </div>
          </div>
          
          {/* 비밀번호 확인 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">비밀번호 확인</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faLock} className="!text-gray-400" />
              </div>
              <input
                type="password"
                value={pwCheck}
                onChange={onChangePwCheck}
                placeholder="비밀번호를 다시 한번 입력해 주세요"
                className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                style={{ fontFamily: 'sans-serif', letterSpacing: '0.1em' }}
                required
              />
            </div>
          </div>
          </div>
          
          {/* 사업자정보 섹션 */}
          <div className="!pt-4 !border-t !border-gray-200">
            <h3 className="!text-lg !font-semibold !text-gray-800 !mb-4 !flex !items-center">
              <FontAwesomeIcon icon={faBuilding} className="!mr-2 !text-indigo-500" />
              사업자정보
            </h3>

          {/* 사업자 명 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">사업자 명</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faIdCard} className="!text-gray-400" />
              </div>
              <input
                  type="text"
                  value={companyName}
                  onChange={onChangeCompanyName}
                  placeholder="사업자 명을 입력해 주세요"
                  className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                  required
              />
            </div>
          </div>

          {/* 대표자 명 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">대표자 명</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faIdCard} className="!text-gray-400" />
              </div>
              <input
                  type="text"
                  value={ceoName}
                  onChange={onChangeCeoName}
                  placeholder="대표자 명을 입력해 주세요"
                  className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                  required
              />
            </div>
          </div>

          {/* 사업자 전화번호 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">사업자 전화번호</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faPhone} className="!text-gray-400" />
              </div>
              <input
                  type="tel"
                  value={companyTelNo}
                  onChange={onChangeCompanyTelNo}
                  placeholder="사업자 전화번호를 입력해 주세요"
                  className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                  required
              />
            </div>
          </div>

          {/* 사업자 번호 등록 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">사업자 등록 번호</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faBuilding} className="!text-gray-400" />
              </div>
              <input
                  type="text"
                  value={businessNumber}
                  onChange={onChangeBusinessNumber}
                  placeholder="사업자 등록번호를 입력해 주세요 (예: 123-45-67890)"
                  className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                  required
              />
            </div>
          </div>

          {/* 사업자 주소 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">사업자 주소</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faIdCard} className="!text-gray-400" />
              </div>
              <input
                  type="text"
                  value={address}
                  onChange={onChangeAddress}
                  placeholder="사업자 주소를 입력해 주세요"
                  className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                  required
              />
            </div>
          </div>

          {/* 세금계산서 이메일 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">세금계산서 이메일</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faEnvelope} className="!text-gray-400" />
              </div>
              <input
                  type="email"
                  value={email}
                  onChange={onChangeEmail}
                  placeholder="세금계산서 발급용 이메일을 입력해 주세요"
                  className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                  required
              />
            </div>
          </div>

          {/* 당근 비즈프로필 명 */}
          <div className="!mb-4">
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-2">브랜드 명</label>
            <div className="!relative">
              <div className="!absolute !inset-y-0 !left-0 !pl-3 !flex !items-center !pointer-events-none">
                <FontAwesomeIcon icon={faStore} className="!text-gray-400" />
              </div>
              <input
                  type="text"
                  value={bizProfileName}
                  onChange={onChangeBizProfileName}
                  placeholder="브랜드 명을 입력해 주세요"
                  className="!w-full !pl-10 !pr-3 !py-3 !border !border-gray-300 !rounded-lg !focus:outline-none !focus:ring-2 !focus:ring-indigo-500 !focus:border-transparent !transition-all"
                  required
              />
            </div>
          </div>
          </div>
          
          {/* 이용약관 동의 */}
          <div className="!pt-4 !border-t !border-gray-200">
            <div className="!flex !items-start !space-x-3">
              <div className="!flex !items-center !h-5">
                <input
                  id="terms-agreement"
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="!w-4 !h-4 !text-indigo-600 !bg-gray-100 !border-gray-300 !rounded !focus:ring-indigo-500 !focus:ring-2"
                />
              </div>
              <div className="!text-sm">
                <label htmlFor="terms-agreement" className="!font-medium !text-gray-700">
                  이용약관에 동의합니다
                  <span className="!text-red-500 !ml-1">*</span>
                </label>
                <p className="!text-gray-500 !text-xs !mt-1">
                  회원가입을 위해 아래 약관을 확인하고 동의해 주세요.
                </p>
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="!inline-flex !items-center !text-indigo-600 hover:!text-indigo-800 !text-xs !mt-2 !underline"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="!w-3 !h-3 !mr-1" />
                  이용약관 자세히 보기
                </button>
              </div>
            </div>
          </div>
          
          {/* 버튼 */}
          <div className="!pt-4">
            <button
              type="submit"
              disabled={!agreeToTerms}
              className={`!w-full !py-3 !px-4 !font-medium !rounded-lg !shadow-lg !transition-all !duration-200 ${
                agreeToTerms
                  ? '!bg-gradient-to-r !from-indigo-500 !to-purple-600 !text-white hover:!shadow-xl !transform hover:!-translate-y-0.5'
                  : '!bg-gray-300 !text-gray-500 !cursor-not-allowed'
              }`}
            >
              회원가입
            </button>
          </div>
        </form>
      </div>

      {/* 이용약관 모달 */}
      {showTermsModal && (
        <div className="!fixed !inset-0 !z-60 !flex !items-center !justify-center !p-4 !bg-black !bg-opacity-50">
          <div className="!bg-white !rounded-2xl !shadow-2xl !w-full !max-w-2xl !max-h-[80vh] !overflow-hidden">
            {/* 약관 모달 헤더 */}
            <div className="!px-6 !py-4 !border-b !border-gray-200 !flex !items-center !justify-between">
              <h3 className="!text-lg !font-bold !text-gray-800">Fortune LAB 이용약관</h3>
              <button
                onClick={() => setShowTermsModal(false)}
                className="!w-8 !h-8 !rounded-full !bg-gray-100 hover:!bg-gray-200 !flex !items-center !justify-center !transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="!text-gray-600 !text-sm" />
              </button>
            </div>
            
            {/* 약관 내용 */}
            <div className="!px-6 !py-4 !overflow-y-auto !max-h-[60vh]">
              <div className="!space-y-4 !text-sm !text-gray-700">
                <section>
                  <h4 className="!font-bold !text-base !mb-2">제1조 (목적)</h4>
                  <p>본 약관은 Fortune LAB(이하 "회사")이 제공하는 사주 및 운세 관련 서비스(이하 "서비스")의 이용조건 및 절차, 회사와 회원간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.</p>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제2조 (정의)</h4>
                  <ul className="!list-disc !list-inside !space-y-1">
                    <li>"서비스"란 회사가 제공하는 사주, 운세, 궁합 등 점술 관련 콘텐츠 및 관련 서비스를 의미합니다.</li>
                    <li>"회원"이란 본 약관에 동의하고 회사와 서비스 이용계약을 체결한 자를 의미합니다.</li>
                    <li>"가맹점"이란 회사의 서비스를 이용하여 고객에게 점술 서비스를 제공하는 사업자를 의미합니다.</li>
                  </ul>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제3조 (약관의 게시와 개정)</h4>
                  <p>회사는 본 약관을 회원이 쉽게 알 수 있도록 서비스 화면에 게시합니다. 회사는 필요한 경우 관련 법령을 위배하지 않는 범위에서 본 약관을 개정할 수 있습니다.</p>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제4조 (서비스의 제공)</h4>
                  <ul className="!list-disc !list-inside !space-y-1">
                    <li>사주 및 운세 상담 서비스</li>
                    <li>궁합 및 택일 서비스</li>
                    <li>온라인 점술 플랫폼 서비스</li>
                    <li>기타 회사가 정하는 서비스</li>
                  </ul>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제5조 (회원가입)</h4>
                  <p>서비스를 이용하고자 하는 자는 회사가 정한 가입 양식에 따라 회원정보를 기입한 후 본 약관에 동의한다는 의사표시를 함으로써 회원가입을 신청합니다.</p>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제6조 (개인정보보호)</h4>
                  <p>회사는 관련 법령이 정하는 바에 따라 회원의 개인정보를 보호하기 위해 노력합니다. 개인정보의 보호 및 사용에 대해서는 관련 법령 및 회사의 개인정보보호정책이 적용됩니다.</p>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제7조 (회원의 의무)</h4>
                  <ul className="!list-disc !list-inside !space-y-1">
                    <li>회원은 관계법령, 본 약관의 규정, 이용안내 및 서비스상에 공지한 주의사항을 준수하여야 합니다.</li>
                    <li>회원은 서비스를 이용하여 얻은 정보를 회사의 사전 승낙 없이 복사, 복제, 변경, 번역, 출판, 방송 기타의 방법으로 사용하거나 이를 타인에게 제공할 수 없습니다.</li>
                  </ul>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제8조 (서비스 이용제한)</h4>
                  <p>회사는 회원이 본 약관의 의무를 위반하거나 서비스의 정상적인 운영을 방해한 경우, 서비스 이용을 제한하거나 중지할 수 있습니다.</p>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제9조 (면책조항)</h4>
                  <p>회사가 제공하는 점술 서비스는 참고용이며, 이를 근거로 한 회원의 판단이나 행동에 대해 회사는 책임을 지지 않습니다.</p>
                </section>

                <section>
                  <h4 className="!font-bold !text-base !mb-2">제10조 (준거법 및 관할법원)</h4>
                  <p>본 약관은 대한민국 법령에 의하여 규정되고 이행되며, 서비스 이용으로 발생한 분쟁에 대해서는 회사 소재지를 관할하는 법원을 관할법원으로 합니다.</p>
                </section>
              </div>
            </div>
            
            {/* 약관 모달 버튼 */}
            <div className="!px-6 !py-4 !border-t !border-gray-200 !flex !justify-end !space-x-3">
              <button
                onClick={() => setShowTermsModal(false)}
                className="!px-4 !py-2 !text-gray-600 !border !border-gray-300 !rounded-lg hover:!bg-gray-50 !transition-colors"
              >
                닫기
              </button>
              <button
                onClick={() => {
                  setAgreeToTerms(true);
                  setShowTermsModal(false);
                }}
                className="!px-4 !py-2 !bg-indigo-600 !text-white !rounded-lg hover:!bg-indigo-700 !transition-colors !flex !items-center"
              >
                <FontAwesomeIcon icon={faCheck} className="!mr-2" />
                동의하고 닫기
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 회원가입 에러 모달 */}
      <RegistrationErrorModal
        isOpen={showErrorModal}
        setIsOpen={setShowErrorModal}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default RegistrationModal;
